import {
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';

import type { RootState } from '@/store';
import { API_V1_BASE } from '@/lib/env';
import { createBaseQueryWithReauth } from '@/lib/auth-refresh';

/**
 * RTK Query base API slice.
 *
 * Auth model:
 * - Access token lives in Redux (in-memory) and is sent as `Authorization: Bearer`.
 * - Refresh token is an HttpOnly cookie (sent automatically via `credentials: 'include'`).
 * - On 401, we transparently call /auth/refresh once, then retry the original request.
 */

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_V1_BASE,
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;
    const token = state.auth.accessToken;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    const companyId = state.auth.user?.companyId;
    if (companyId) {
      headers.set('X-Company-Id', companyId);
    }
    return headers;
  },
});

const baseQueryWithReauth = createBaseQueryWithReauth(rawBaseQuery);

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Auth',
    'Company',
    'Team',
    'User',
    'Role',
    'Permission',
    'Contact',
    'Transaction',
    'Product',
    'Status',
    'Tag',
    'ImportJob',
    'ExportJob',
    'Conversation',
    'Message',
    'Template',
    'Campaign',
    'CampaignContact',
    'WhatsAppIntegration',
    'EmailIntegration',
    'AiIntegration',
    'AiSession',
    'AiQueryLog',
    'Notification',
    'Activity',
    'AuditLog',
    'AiUsageLog',
    'Dashboard',
    'CompanySettings',
    'LoanApplicant',
    'BusinessEntity',
    'LoanDocument',
    'DocumentTemplate',
  ],
  endpoints: () => ({}),
});

export const api = apiSlice;
export default apiSlice;
